import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import get from 'lodash/get';
import app from 'general/js/app';
import gtm from 'general/js/gtm';
import { getPlayerTeeTime } from 'general/js/helpers';
import { CountryFlag } from 'project/country-flag';
import { ScoreTotalCell } from 'components/scores';
import FavoritePlayerButton from 'components/favorite-player-button';
import { LiveTabsNav } from 'components/live-tabs';
import { LIVEPLAYER_BG_IMAGE_PATH, PLAYER_SILHOUETTE_ON_SCORECARD } from 'general/js/constants';
import { playerSitecoreData, playerStatusCodes } from './enums';

const LivePlayerHero = (
    {
        scoringLeaderboard,
        scoringPlayer,
        tabs,
        selectedTabId,
        setTab
    }
) => {
    if (!scoringPlayer) return null;

    const sitecoreData = scoringPlayer.sitecoreData || playerSitecoreData;
    const { firstName, lastName } = sitecoreData;
    const playerStatuses = app.state.playerStatuses || [];
    const isOnHole = scoringPlayer.hole && scoringPlayer.hole !== 'F';
    const displayPosition = get(scoringPlayer, 'position.displayValue') || null;
    const teeTime = getPlayerTeeTime(
        scoringLeaderboard.currentRound,
        scoringLeaderboard.currentRoundStatus,
        scoringPlayer.rounds
    );

    const tenthTeeStartMarkup = scoringPlayer.startTee === 10 ? '*' : '';
    const formattedTeeTime = teeTime &&
        `${format(teeTime.replace('Z', ''), 'ddd D h:mm a')}${tenthTeeStartMarkup} BST`;

    const playerCutOutSrc = sitecoreData.cutoutSrc || PLAYER_SILHOUETTE_ON_SCORECARD;

    const getPlayerStatusOrPar = (statusId, toPar, positionValue) => {
        if (!positionValue) return null;

        return statusId !== playerStatusCodes.ACTIVE ?
            positionValue.displayValue : toPar;
    };

    const trackFeaturedGroup = () => {
        gtm.push('Player Card', 'Featured Group', `${firstName} ${lastName}`);
    };

    const getPlayerStatusText = (statusId) => {
        const foundPlayerStatus = playerStatuses.find((playerStatus) =>
            playerStatus.id === statusId);

        return (foundPlayerStatus && foundPlayerStatus.text) || '';
    };

    return (
        <div className="live-player-hero">
            <div className="live-player-hero__bg of-cover">
                <img src={LIVEPLAYER_BG_IMAGE_PATH} alt="" />
            </div>
            <div className="live-player-hero__photo of-contain">
                <img src={playerCutOutSrc} alt="" />
            </div>
            <div className="live-player-hero__bar">
                <div>
                    <FavoritePlayerButton
                        trackCategory="Player Card"
                        trackAction="Favourites"
                        trackLabel={`${firstName} ${lastName}`}
                        playerId={scoringPlayer.id.toString()}
                        theme={{ isRound: true, isWhite: true }}
                    />
                </div>
                {
                    sitecoreData.featuredGroupLiveUrl &&
                    <a
                        className="live-player-hero__featured"
                        href={sitecoreData.featuredGroupLiveUrl}
                        onClick={trackFeaturedGroup}
                    >
                        <div className="button-round button-round--base is-border-solid-white">
                            <svg className="icon" width="14" height="14" focusable="false">
                                <use xlinkHref="#icon-watch"></use>
                            </svg>
                        </div>
                        <span className="live-player-hero__featured-label">WATCH ON FEATURED GROUP</span>
                    </a>
                }
            </div>
            <div className="live-player-hero__row">
                <ScoreTotalCell childCss='live-player-hero__score' par={getPlayerStatusOrPar(scoringPlayer.status, scoringPlayer.toPar, scoringPlayer.position)} />
                <div>
                    <CountryFlag image={sitecoreData.countryFlagSrc} />
                    <h1 className="live-player-hero__title">
                        <span className="live-player-hero__firstname">{sitecoreData.firstName}</span>
                        <span className="live-player-hero__lastname">{sitecoreData.lastName}
                            {sitecoreData.isAmateur && <span className="live-player-hero__amateur">(a)</span>}
                        </span>
                    </h1>
                </div>
            </div>
            <div className="live-player-hero__extra">
                {
                    scoringPlayer.status === playerStatusCodes.ACTIVE &&
                    <React.Fragment>
                        {
                            displayPosition &&
                            <div className="live-player-hero__position copy">
                                <b>Position {displayPosition}</b>
                            </div>
                        }
                        {
                            !formattedTeeTime &&
                            scoringPlayer.playerStatusText &&
                            <div className="live-player-hero__status">{scoringPlayer.playerStatusText}</div>
                        }
                        {
                            !isOnHole &&
                            formattedTeeTime &&
                            <div className="live-player-hero__status">
                                <svg className="icon" width="14" height="14" focusable="false">
                                    <use xlinkHref="#icon-clock"></use>
                                </svg>
                                <span><b>Tee Time</b> {formattedTeeTime}</span>
                            </div>
                        }
                    </React.Fragment>
                }
                {
                    scoringPlayer.status !== playerStatusCodes.ACTIVE &&
                    <div className="live-player-hero__status">{getPlayerStatusText(scoringPlayer.status)}</div>
                }
            </div>
            {
                tabs.length > 1 &&
                <div className="live-player-hero__tabs">
                    <LiveTabsNav tabs={tabs} activeTabId={selectedTabId} onTabClick={setTab} />
                </div>
            }
        </div>
    );
};

LivePlayerHero.propTypes = {
    scoringLeaderboard: PropTypes.object,
    scoringPlayer: PropTypes.object
};

export default LivePlayerHero;
